import dayjs from 'dayjs'
import { FunctionComponent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DmFilter, useContentSearchQuery, useGetProfilesByIdsQuery } from '../api/client'
import ProfileNameDisplay, {
    ProfileWithOptionalName
} from '../components/account/settings/shadow-banning/ProfileNameDisplay'
import ContentList from '../components/content/ContentList'
import PageMasthead from '../components/PageMasthead'
import useContentSearchParams from '../hooks/useContentQueryParams'

/**
 * Displays all content from a specific author profile.
 *
 * Expects the authorProfileId and accountId on the URL path.
 * e.g. /author/123/account/456
 *
 * Displays using the ContentList component, which does all the heavy lifting.
 */
const AuthorContentPage: FunctionComponent = () => {
    const navigate = useNavigate()
    const { authorProfileId, accountId } = useParams<{
        authorProfileId: string
        accountId: string
    }>()

    const profileId = authorProfileId ? Number(authorProfileId) : undefined
    const parsedAccountId = accountId ? Number(accountId) : undefined

    const { searchParameters } = useContentSearchParams()

    if (profileId) {
        searchParameters.authorProfileIds = [profileId]
    }
    if (parsedAccountId) {
        searchParameters.selectedAccountIds = [parsedAccountId]
    }

    searchParameters.owned = undefined
    // Use a wide time range to capture all content
    searchParameters.startDateTime = dayjs('2019-01-01')
    searchParameters.endDateTime = dayjs('3000-01-02')

    const { data: profileData } = useGetProfilesByIdsQuery({
        variables: {
            profileIds: profileId ? [profileId] : [],
            accountId: parsedAccountId
        },
        skip: !profileId
    })

    const profile = profileData?.getProfilesByIds?.[0] as ProfileWithOptionalName | undefined

    // Fetch first content item to get author name as fallback
    const { data: contentData, loading: contentLoading } = useContentSearchQuery({
        variables: {
            params: {
                accountIds: parsedAccountId ? [parsedAccountId] : [],
                authorProfileIds: profileId ? [profileId] : [],
                dmFilter: DmFilter.CommentsOnly,
                timeDuration: 'P5Y',
                maxResults: 1
            }
        },
        skip: !profileId || !parsedAccountId
    })

    const firstContent = contentData?.contentSearch?.[0]
    const profileContentData = firstContent
        ? {
              serviceAuthorName: firstContent.serviceAuthorName,
              serviceAuthorDisplayUsername: firstContent.serviceAuthorDisplayUsername
          }
        : undefined
    const serviceName = firstContent?.socialMediaServiceName

    return (
        <>
            <PageMasthead
                title={
                    profile ? (
                        <>
                            Comments by{' '}
                            <ProfileNameDisplay
                                profile={profile}
                                contentData={profileContentData}
                                loading={contentLoading}
                            />
                        </>
                    ) : (
                        'Author comments'
                    )
                }
                tabTitle="Author comments"
            />
            <div className="self-center w-full">
                <div className="py-6 px-2">
                    {profile && (
                        <div className="mb-4">
                            {profile.profileUrl &&
                                (serviceName?.toLowerCase() === 'facebook' ? (
                                    <span
                                        className="text-sm text-gray-400 cursor-not-allowed"
                                        data-tooltip-id="tooltip-id"
                                        data-tooltip-content="Not currently supported"
                                    >
                                        View profile on Facebook
                                    </span>
                                ) : (
                                    <a
                                        href={profile.profileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-sm text-primary-600 hover:text-primary-500"
                                    >
                                        View profile on {serviceName ?? 'platform'}
                                    </a>
                                ))}
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="font-semibold text-primary-600 hover:text-primary-500"
                    >
                        Back
                    </button>
                </div>

                <ContentList
                    searchParameters={searchParameters}
                    showOwningAccount={true}
                    enableBulkActions={false}
                />
            </div>
        </>
    )
}

export default AuthorContentPage
